<template>
    <div class="field">
        <label class="label">{{ translate("discipline_packages.kata_number_of_judges") }}</label>
        <div class="control">
            <div class="select">
                <select v-model="numberOfJudges">
                    <option v-for="n in [3, 4, 5, 6, 7]" :key="n" :value="n">{{ n }}</option>
                </select>
            </div>
        </div>
    </div>
    <div class="field">
        <div class="control">
            <label class="checkbox">
                <input type="checkbox" v-model="showTimer" />
                {{ translate("discipline_packages.kata_show_timer") }}
            </label>
        </div>
    </div>
    <div class="field">
        <div class="control">
            <label class="checkbox">
                <input type="checkbox" v-model="discardMinMax" />
                {{ translate("discipline_packages.kata_discard_min_max") }}
            </label>
        </div>
    </div>
    <div class="field is-flex is-align-items-center">
        <p class="mr-3 mb-0">{{ translate("discipline_packages.kata_calculation_method") }}:</p>
        <div class="control mr-3">
            <Radio v-model="calculationMethod" name="kata-calculation-method" initial-value="sum">
                {{ translate("discipline_packages.kata_calculation_sum") }}
            </Radio>
        </div>
        <div class="control">
            <Radio v-model="calculationMethod" name="kata-calculation-method" initial-value="average">
                {{ translate("discipline_packages.kata_calculation_average") }}
            </Radio>
        </div>
    </div>
    <div class="field">
        <div class="control">
            <label class="checkbox">
                <input type="checkbox" v-model="showFullClassificationAfterPerformance" />
                {{ translate("discipline_packages.kata_show_full_classification") }}
            </label>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { computed } from "vue";
    import { translate } from "../../Common/translations";
    import { useDisciplinePackagesStore } from "../disciplinePackagesStore";
    import { KataCalculationMethod, ScoreboardSettings } from "../../Api/disciplinePackages";
    import Radio from "../../Common/Radio.vue";

    const store = useDisciplinePackagesStore();

    const numberOfJudges = computed<number>({
        get: () => store.activeScoreboardSettings.numberOfJudges ?? 7,
        set: (value: number) => {
            const current = store.activeScoreboardSettings;
            if (store.activeTabIndex !== null) {
                store.disciplineOverrideConfigs[store.activeTabIndex].scoreboardSettings = {
                    ...current,
                    numberOfJudges: value,
                };
            } else {
                store.disciplineDefaultConfig.scoreboardSettings = {
                    ...current,
                    numberOfJudges: value,
                } satisfies ScoreboardSettings;
            }
        },
    });

    const showTimer = computed<boolean>({
        get: () => store.activeScoreboardSettings.showTimer ?? false,
        set: (value: boolean) => {
            const current = store.activeScoreboardSettings;
            if (store.activeTabIndex !== null) {
                store.disciplineOverrideConfigs[store.activeTabIndex].scoreboardSettings = {
                    ...current,
                    showTimer: value,
                };
            } else {
                store.disciplineDefaultConfig.scoreboardSettings = { ...current, showTimer: value };
            }
        },
    });

    const discardMinMax = computed<boolean>({
        get: () => store.activeScoreboardSettings.discardMinMax ?? true,
        set: (value: boolean) => {
            const current = store.activeScoreboardSettings;
            if (store.activeTabIndex !== null) {
                store.disciplineOverrideConfigs[store.activeTabIndex].scoreboardSettings = {
                    ...current,
                    discardMinMax: value,
                };
            } else {
                store.disciplineDefaultConfig.scoreboardSettings = { ...current, discardMinMax: value };
            }
        },
    });

    const calculationMethod = computed<KataCalculationMethod>({
        get: () => store.activeScoreboardSettings.calculationMethod ?? "sum",
        set: (value: KataCalculationMethod) => {
            const current = store.activeScoreboardSettings;
            if (store.activeTabIndex !== null) {
                store.disciplineOverrideConfigs[store.activeTabIndex].scoreboardSettings = {
                    ...current,
                    calculationMethod: value,
                };
            } else {
                store.disciplineDefaultConfig.scoreboardSettings = { ...current, calculationMethod: value };
            }
        },
    });

    const showFullClassificationAfterPerformance = computed<boolean>({
        get: () => store.activeScoreboardSettings.showFullClassificationAfterPerformance ?? false,
        set: (value: boolean) => {
            const current = store.activeScoreboardSettings;
            if (store.activeTabIndex !== null) {
                store.disciplineOverrideConfigs[store.activeTabIndex].scoreboardSettings = {
                    ...current,
                    showFullClassificationAfterPerformance: value,
                };
            } else {
                store.disciplineDefaultConfig.scoreboardSettings = {
                    ...current,
                    showFullClassificationAfterPerformance: value,
                };
            }
        },
    });
</script>
